import { REPORT_CHART_FEEDBACK_BASED_MEASURES } from '@vision/ui/constants';
import { ReportChartFeedbackBaseType } from '@vision/ui/enums';
import { ReportChartConfiguration, ReportChartMeasureType } from '@vision/ui/interfaces';
import { t } from 'i18next';
import { array, lazy, object, string } from 'yup';
import { FeedbackBasedQuestionBasedMeasureRenderer, TableChart1CompareRenderer } from '../../components';
import {
  DefaultReportChartCompareSchema,
  DefaultReportChartThemeFont,
  GRAPHIC_TITLE_MAX_CHAR_COUNT,
} from '../default-chart.config';
import { GaugeChart1Measures } from './gauge-chart1.measures';

export const GaugeChart1DefaultConfiguration: ReportChartConfiguration = {
  validationSchema: object().shape({
    selectedDate: object().shape({
      fromDate: string().required('validation:rules.generic.required'),
      toDate: string().required('validation:rules.generic.required'),
      dateRange: string().optional(),
    }),
    selectedMeasure: string().required('validation:rules.generic.required'),
    selectedTheme: object().shape({
      title: object().shape({
        label: string()
          .max(
            GRAPHIC_TITLE_MAX_CHAR_COUNT,
            t('validation:rules.generic.max', {
              count: GRAPHIC_TITLE_MAX_CHAR_COUNT,
            }),
          )
          .required('validation:rules.reports.selectedTheme.title.label.required'),
      }),
    }),
    selectedCompare: array().of(string()),
    selectedCompareCriteria: object().shape({
      ...DefaultReportChartCompareSchema,
    }),
    selectedFeedbackBasedQuestionBased: lazy(() =>
      string().when(['selectedMeasure'], {
        is: (selectedMeasure: ReportChartMeasureType) => REPORT_CHART_FEEDBACK_BASED_MEASURES.includes(selectedMeasure),
        then: (selectedFeedbackBasedQuestionBased) => selectedFeedbackBasedQuestionBased.required(),
        otherwise: (selectedFeedbackBasedQuestionBased) => selectedFeedbackBasedQuestionBased.notRequired(),
      }),
    ),
    selectedCompareFlowIds: lazy(() =>
      array().when(['selectedFeedbackBasedQuestionBased'], {
        is: (selectedFeedbackBasedQuestionBased: ReportChartFeedbackBaseType) =>
          selectedFeedbackBasedQuestionBased === ReportChartFeedbackBaseType.QUESTION_BASED,
        then: (selectedCompareFlowIds) => selectedCompareFlowIds.required().min(1),
        otherwise: (selectedCompareFlowIds) => selectedCompareFlowIds.notRequired(),
      }),
    ),
    selectedCompareQuestionIds: lazy(() =>
      array().when(['selectedFeedbackBasedQuestionBased'], {
        is: (selectedFeedbackBasedQuestionBased: ReportChartFeedbackBaseType) =>
          selectedFeedbackBasedQuestionBased === ReportChartFeedbackBaseType.QUESTION_BASED,
        then: (selectedCompareQuestionIds) => selectedCompareQuestionIds.required().min(1),
        otherwise: (selectedCompareQuestionIds) => selectedCompareQuestionIds.notRequired(),
      }),
    ),
  }),
  measures: GaugeChart1Measures,
  compareRendererComponent: TableChart1CompareRenderer,
  measureRendererComponent: FeedbackBasedQuestionBasedMeasureRenderer,
  compareHeader: {
    labelKey: 'page-reports:compare',
    showIcon: true,
    showAsterisk: false,
  },
  defaultFeedbackBasedQuestionBased: ReportChartFeedbackBaseType.FEEDBACK_BASED,
  compareMultiple: true,
  customize: {
    active: (values) => !!values.selectedMeasure,
    transactionalDate: {
      active: false,
    },
    feedbackCount: {
      active: false,
    },
  },
  defaultTheme: {
    active: (values) => !!values.selectedMeasure,
    title: {
      ...DefaultReportChartThemeFont,
      label: '',
    },
    dataLabels: {
      ...DefaultReportChartThemeFont,
      active: false,
      fontSize: 20,
      maxMinValue: {
        max: null,
        min: null,
      },
    },
  },
};
